import qs from 'qs'
import { useMutation, UseMutationOptions, useQuery } from '@tanstack/react-query'
import { queryClient } from '~/common/react-query/queryClient'
import { OffsetLimitPaginationResponse } from '~/api/common'
import fetch from '~/common/fetch'

export enum ComponentType {
  Expenses = 'Expenses',
  Invoices = 'Invoices',
  JobOffer = 'JobOffer',
  Jobs = 'Jobs',
  OfferAcceptance = 'OfferAcceptance',
  Payment = 'Payment',
  Sow = 'Sow',
  SOWClosure = 'Sow_Closure',
  SOWReopen = 'Sow_Reopen',
  SowChangeOrder = 'Sow_Change_Order',
  SowPaymentRequest = 'Sow_Payment_Request',
  Timesheets = 'Timesheets',
  WorkerTrackingRevision = 'WorkerTrackingRevision',
  WorkOrder = 'WorkOrder',
  ScheduledBill = 'ScheduledBill',
  Candidate = 'Candidate'
}

export enum ComponentSubtype {
  ExpenseAndAdjustment = 'Expense & Adjustment',
  Invoice = 'Invoice',
  OfferAcceptance = 'Initial Internal Approval',
  JobOfferRelease = 'Job Offer Release',
  JobPublish = 'Job Publish',
  JobChange = 'Job Change',
  Onboarding = 'Onboarding',
  ScheduledMilestonePayment = 'Scheduled Milestone Payment',
  SOWClosure = 'SOW Closure',
  SOWReopen = 'SOW Reopen',
  SOWAcceptance = 'SOW Acceptance',
  SOWUnitPayment = 'SOW Unit Payment',
  SOWMilestonePayment = 'SOW Milestone Payment',
  SOWChangeOrder = 'SOW Change Order',
  Timesheets = 'Timesheets',
  WorkerTrackerCreation = 'Worker Tracker Creation',
  WorkerTrackerDateChange = 'Worker Tracker Date Change',
  WorkerTrackerEnd = 'Worker Tracker End',
  WorkOrderModification = 'Work Order Modification',
  WorkOrderEnd = 'Work Order End',
  ScheduledBill = 'Scheduled Bill',
  CandidateModification = 'Candidate Modification'
}

export enum ApprovalModule {
  WorkflowTasks = 'workflow-tasks',
  Expenses = 'expense-reports-approvals',
  Invoices = 'invoices/approvals',
  OfferAcceptance = 'offer_acceptance',
  OfferAcceptanceApprovals = 'offer_acceptance_approvals',
  JobOffer = 'offer_approvals',
  Jobs = 'job_publish_approvals',
  Timesheet = 'time/time_entries_approvals',
  Payment = 'payment_approvals',
  Sow = 'sow-module/sow-approvals',
  SowPaymentRequest = 'sow-module/sow-payments-approvals',
  SowPaymentMilestone = 'sow-module/milestone-approvals',
  SowPaymentMilestoneV2 = 'sow-module/milestonev2-approvals',
  SowChangeOrder = 'sow-module/change-order-approvals',
  SowSchedulePayment = 'sow-module/schedule-payment-approvals',
  SowClosure = 'sow-module/sow-closure-approvals',
  SowReopen = 'sow-module/sow-reopen-approvals',
  SowAdjustApprovedPayment = 'sow-module/sow-adjust-approved-payment-approvals',
  WorkerTrackingRevision = 'worker_tracker_approvals',
  WorkOrder = 'work_order_approvals',
  ScheduledBill = 'scheduled_billing_approvals',
  Candidate = 'candidate_approvals'
}

// Use this type to create "component_type key to K value" objects
export type ComponentTypeMap<K> = { [name in ComponentType]: K }

type Namespace = 'approval'

export type ApprovalTask = {
  task_id: number
  component_id: number
  component_type: ComponentType
  component_subtype: ComponentSubtype
  component_subtype_translated: string
  namespace: Namespace
  task_data: Record<string, unknown>
  user: number
  user_name: string
  description: string
  can_approve: boolean
  can_override_approval: boolean
  resource_path: string
  timestamp: string
  api_module: ApprovalModule
}

type OrderingOption = 'updated_at' | '-updated_at'
export type AllApprovalsParams = {
  show_overridable?: boolean
  component_type?: ComponentType
  ordering?: OrderingOption
  search?: string
  assigned_to_me?: boolean
}
type ApprovalsResponse = OffsetLimitPaginationResponse<ApprovalTask>

interface BulkApprovalData {
  reason: string
  approved: boolean
  task_ids: number[]
}

interface BulkApprovalResponse {
  task_id: string
}

export interface ApprovalPermissionsResponse {
  has_approval_rules_write: boolean
  has_approval_rules_read: boolean
  has_approval_criteria_resolvers_permission: boolean
  has_bulk_approve_permission: boolean
  has_approval_diagnostics_permission: boolean
}

export const approvalsApi = {
  all: (apiParams: AllApprovalsParams): Promise<ApprovalsResponse> =>
    fetch.get(`/api/v2/approval-tasks/?${qs.stringify(apiParams)}`),
  permissions: (): Promise<ApprovalPermissionsResponse> => fetch.get('/api/v2/workflow_approval_permissions/'),
  bulk_approval: (data: BulkApprovalData) => fetch.post('/api/v2/bulk_approval/', data)
}

export const APPROVALS_LIST_QUERY_KEY = 'approvals/all'

export const useApprovalsQuery = (apiParams: AllApprovalsParams) =>
  useQuery({
    queryKey: [APPROVALS_LIST_QUERY_KEY, apiParams],
    queryFn: () => approvalsApi.all(apiParams),
    keepPreviousData: true
  })

export const invalidateApprovalsQuery = async (apiParams: AllApprovalsParams) => {
  await queryClient.invalidateQueries([APPROVALS_LIST_QUERY_KEY, apiParams])
}

const APPROVAL_PERMISSIONS_QUERY_KEY = 'approvals/permissions'

export const useGetApprovalPermissionsQuery = () =>
  useQuery({ queryKey: [APPROVAL_PERMISSIONS_QUERY_KEY], queryFn: approvalsApi.permissions })

export const useBulkApprovalMutation = (
  opts: UseMutationOptions<BulkApprovalResponse, unknown, BulkApprovalData> = {}
) => useMutation({ mutationFn: approvalsApi.bulk_approval, ...opts })
